/**
 * `/settings/whatsapp` — this deployment's numbers, the pairing flow, and the
 * ban notice.
 *
 * THE RISK NOTICE IS NOT DECORATION. MeshHub speaks WhatsApp's unofficial Web
 * protocol; a linked number can be rate-limited or banned. Somebody about to
 * link a real business number should read that on the screen where they do it,
 * not only in a document they may never open. With several numbers it matters
 * MORE, not less: the warm-up is per number, because WhatsApp has never heard
 * of the new one (docs/whatsapp-multi-account.md §7).
 *
 * THE QR ARRIVES OVER SSE, because it ROTATES: WhatsApp expires each code after
 * about twenty seconds. A unary "give me the QR" would show one that is
 * already dead. The countdown is part of the design — «چرا هیچ اتفاقی
 * نمی‌افتد» is the usual failure here, and a number ticking down answers it.
 *
 * TWO HALVES, NEVER FLATTENED. `label` and `isDefault` are MeshHub's
 * configuration; `linked` and `connected` are the gateway's live answer. A
 * configured-but-unpaired number is the normal state of one nobody has scanned
 * yet, and rendering it as connected would read as «ready to send» for a
 * number that cannot.
 *
 * IT DEGRADES TO ONE NUMBER. A deployment that has never named an account has
 * no rows, sends from the legacy number, and sees the single connection card
 * this screen has always shown — so upgrading changes nothing until somebody
 * chooses to.
 */
import { useMutation, useQuery } from '@apollo/client';
import { Badge, Button, Card, Field, Spinner } from '@meshhub/design-tokens/ui';
import { AlertTriangle, Link2, Pencil, Plus, Star, Unlink } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ConfirmDialog,
  FormDialog,
  MutationError,
  TextField,
} from '../../components/FormKit.web.jsx';
import { QrCode } from '../../components/QrCode.web.jsx';
import { SettingsPage } from '../../components/settings/SettingsKit.web.jsx';
import { getAccessToken } from '../../lib/auth.jsx';
import { formatCount } from '../../lib/format.js';
import {
  CREATE_WHATSAPP_ACCOUNT,
  SET_DEFAULT_WHATSAPP_ACCOUNT,
  SET_WHATSAPP_ACCOUNT_ACTIVE,
  UNLINK_WHATSAPP,
  UPDATE_WHATSAPP_ACCOUNT,
  WHATSAPP_ACCOUNTS_QUERY,
  WHATSAPP_STATUS_QUERY,
} from '../../lib/queries.js';
import { useSession } from '../../lib/session.jsx';
import { BFF_ORIGIN } from '../../lib/runtimeEnv.js';
import { S } from '../../lib/strings.js';

function bffBase() {
  return BFF_ORIGIN;
}

export function WhatsappScreen() {
  // `isAdmin`, not `role` — the session exposes the boolean, and guarding on
  // `role !== 'ADMIN'` reads undefined and shows every admin the wrong panel
  // (CLAUDE.md §15).
  const { isAdmin } = useSession();
  const { data, loading, refetch } = useQuery(WHATSAPP_STATUS_QUERY, {
    // The pairing state changes outside this tab (somebody scans a code on a
    // phone), so the card polls rather than waiting for a refetch nobody will
    // trigger.
    pollInterval: 5000,
  });
  const accountsQuery = useQuery(WHATSAPP_ACCOUNTS_QUERY, {
    variables: { includeInactive: true },
    pollInterval: 5000,
  });

  const refetchAll = useCallback(() => {
    refetch();
    accountsQuery.refetch();
  }, [refetch, accountsQuery]);

  const [pairing, setPairing] = useState(null);
  const [pairingAccount, setPairingAccount] = useState(null);
  const sourceRef = useRef(null);

  const stop = useCallback(() => {
    sourceRef.current?.close();
    sourceRef.current = null;
  }, []);

  // Closing the page must close the stream, or the gateway keeps a pairing
  // attempt alive against a session nobody is watching.
  useEffect(() => stop, [stop]);

  const startPairing = useCallback(
    (accountId, phone) => {
      stop();
      setPairingAccount(accountId ?? '');
      setPairing({ kind: 'PENDING' });
      const params = new URLSearchParams();
      // EventSource cannot set an Authorization header; the BFF verifies this
      // through the identical code path (todo.md).
      const token = getAccessToken();
      if (token) params.set('token', token);
      if (phone?.trim()) params.set('phone', phone.trim());
      // WHICH number is being paired. Absent means the default, which is what
      // a single-number deployment wants and has always got.
      if (accountId) params.set('account', accountId);
      const source = new EventSource(
        `${bffBase()}/integrations/whatsapp/pairing?${params}`,
      );
      sourceRef.current = source;
      source.addEventListener('pairing', (event) => {
        const update = JSON.parse(event.data);
        setPairing(update);
        if (['SUCCESS', 'TIMEOUT', 'ERROR'].includes(update.kind)) {
          stop();
          refetchAll();
        }
      });
      source.onerror = () => {
        setPairing({ kind: 'ERROR', message: S.app.unknownError });
        stop();
      };
    },
    [refetchAll, stop],
  );

  const status = data?.whatsappStatus;
  const accounts = accountsQuery.data?.whatsappAccounts ?? [];
  const sandbox = status && status.provider !== 'hypermeow';

  return (
    <SettingsPage title={S.whatsapp.title}>
      <Card className="flex flex-col gap-3 border-warning">
        <p className="flex items-center gap-2 font-bold text-warning">
          <AlertTriangle size={16} aria-hidden="true" />
          {S.whatsapp.riskTitle}
        </p>
        <p className="text-sm leading-relaxed text-text-muted">{S.whatsapp.riskBody}</p>
      </Card>

      {loading && !status && <Spinner />}

      {status && (
        <ProviderCard status={status} sandbox={sandbox} accountCount={accounts.length} />
      )}

      {status && accounts.length === 0 && (
        // NOTHING CONFIGURED: the legacy single-connection card, unchanged.
        // This is the state every deployment is in before somebody names a
        // number, and it must keep working exactly as it did.
        <LegacyConnectionCard
          status={status}
          isAdmin={isAdmin}
          pairing={pairing}
          onPair={(phone) => startPairing('', phone)}
          onUnlinked={refetchAll}
        />
      )}

      {accounts.length > 0 && (
        <div className="flex flex-col gap-3">
          {accounts.map((account) => (
            <AccountCard
              key={account.id}
              account={account}
              accounts={accounts}
              isAdmin={isAdmin}
              pairing={pairingAccount === account.accountId ? pairing : null}
              onPair={(phone) => startPairing(account.accountId, phone)}
              onChanged={refetchAll}
            />
          ))}
        </div>
      )}

      {isAdmin && <AddAccountCard empty={accounts.length === 0} onCreated={refetchAll} />}
    </SettingsPage>
  );
}

/** Deployment-wide facts: which transport, and whether sending is switched off. */
function ProviderCard({ status, sandbox, accountCount }) {
  return (
    <Card className="flex flex-wrap items-center gap-3">
      <h2 className="text-md font-bold">{S.whatsapp.connection}</h2>
      {/* «sandbox» and «connected» must never be confusable — the provider is
          named on every screen that can send. */}
      <Badge
        className={
          sandbox ? 'bg-surface-hover text-warning' : 'bg-surface-hover text-success'
        }
      >
        {status.provider}
      </Badge>
      {status.sendDisabled && (
        <Badge className="bg-surface-hover text-danger">{S.whatsapp.sendDisabled}</Badge>
      )}
      {accountCount > 0 && (
        <Badge className="bg-surface-hover">
          {S.whatsapp.accounts} {formatCount(accountCount)}
        </Badge>
      )}
    </Card>
  );
}

/** The screen as it was before multi-account, for a deployment with no rows. */
function LegacyConnectionCard({ status, isAdmin, pairing, onPair, onUnlinked }) {
  const [unlink, unlinkState] = useMutation(UNLINK_WHATSAPP, { onCompleted: onUnlinked });
  const [confirming, setConfirming] = useState(false);
  const [phone, setPhone] = useState('');

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Badge className="bg-surface-hover">
          {status.paired ? S.whatsapp.paired : S.whatsapp.notPaired}
        </Badge>
        <Badge
          className={
            status.connected
              ? 'bg-surface-hover text-success'
              : 'bg-surface-hover text-text-muted'
          }
        >
          {status.connected ? S.whatsapp.connected : S.whatsapp.disconnected}
        </Badge>
      </div>

      <StatusFields status={status} />

      {isAdmin && (
        <div className="flex flex-wrap items-end gap-3 border-t border-border pt-4">
          {!status.paired && (
            <>
              <Button variant="primary" onClick={() => onPair(phone)}>
                <Link2 size={14} aria-hidden="true" />
                {S.whatsapp.pair}
              </Button>
              <TextField
                label={S.whatsapp.phoneForCode}
                hint={S.whatsapp.codeAlternative}
                value={phone}
                dir="ltr"
                onChange={(event) => setPhone(event.target.value)}
              />
            </>
          )}
          {status.paired && (
            <Button variant="danger" onClick={() => setConfirming(true)}>
              <Unlink size={14} aria-hidden="true" />
              {S.whatsapp.unlink}
            </Button>
          )}
        </div>
      )}

      {pairing && <PairingPanel pairing={pairing} />}
      <MutationError error={unlinkState.error} />

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={S.whatsapp.unlink}
        body={S.whatsapp.unlinkConfirm}
        confirmLabel={S.whatsapp.unlink}
        destructive
        onConfirm={async () => {
          await unlink({ variables: { accountId: '' } });
          setConfirming(false);
        }}
      />
    </Card>
  );
}

/** One named number. */
function AccountCard({ account, accounts, isAdmin, pairing, onPair, onChanged }) {
  const [unlink, unlinkState] = useMutation(UNLINK_WHATSAPP, { onCompleted: onChanged });
  const [setDefault, defaultState] = useMutation(SET_DEFAULT_WHATSAPP_ACCOUNT, {
    onCompleted: onChanged,
  });
  const [setActive, activeState] = useMutation(SET_WHATSAPP_ACCOUNT_ACTIVE, {
    onCompleted: onChanged,
  });
  const [confirming, setConfirming] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [phone, setPhone] = useState('');

  // The server refuses this; saying so BEFORE the click is the difference
  // between a disabled button with a reason and an error nobody expected.
  const otherActive = accounts.some((a) => a.id !== account.id && a.isActive);
  const blockedFromDeactivating = account.isDefault && otherActive;

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-md font-bold">{account.label}</h2>
        <code dir="ltr" className="text-xs text-text-muted">
          {account.accountId}
        </code>
        {account.isDefault && (
          <Badge className="bg-surface-hover text-success">
            <Star size={12} aria-hidden="true" /> {S.whatsapp.isDefault}
          </Badge>
        )}
        {!account.isActive && (
          <Badge className="bg-surface-hover text-text-muted">{S.whatsapp.inactive}</Badge>
        )}
        <Badge className="bg-surface-hover">
          {account.linked ? S.whatsapp.paired : S.whatsapp.notLinkedYet}
        </Badge>
        {account.linked && (
          <Badge
            className={
              account.connected
                ? 'bg-surface-hover text-success'
                : 'bg-surface-hover text-text-muted'
            }
          >
            {account.connected ? S.whatsapp.connected : S.whatsapp.disconnected}
          </Badge>
        )}
        {account.sendDisabled && (
          <Badge className="bg-surface-hover text-danger">{S.whatsapp.sendDisabled}</Badge>
        )}
      </div>

      <StatusFields status={account} />

      {!account.linked && (
        <p className="text-sm text-warning">{S.whatsapp.perAccountWarmup}</p>
      )}

      {isAdmin && (
        <div className="flex flex-wrap items-end gap-3 border-t border-border pt-4">
          {!account.linked && (
            <>
              <Button variant="primary" onClick={() => onPair(phone)}>
                <Link2 size={14} aria-hidden="true" />
                {S.whatsapp.pair}
              </Button>
              <TextField
                label={S.whatsapp.phoneForCode}
                hint={S.whatsapp.codeAlternative}
                value={phone}
                dir="ltr"
                onChange={(event) => setPhone(event.target.value)}
              />
            </>
          )}
          {account.linked && (
            <Button variant="danger" onClick={() => setConfirming(true)}>
              <Unlink size={14} aria-hidden="true" />
              {S.whatsapp.unlink}
            </Button>
          )}
          <Button variant="ghost" onClick={() => setRenaming(true)}>
            <Pencil size={14} aria-hidden="true" />
            {S.whatsapp.rename}
          </Button>
          {!account.isDefault && account.isActive && (
            <Button
              variant="ghost"
              onClick={() => setDefault({ variables: { id: account.id } })}
            >
              <Star size={14} aria-hidden="true" />
              {S.whatsapp.makeDefault}
            </Button>
          )}
          <Button
            variant="ghost"
            disabled={blockedFromDeactivating}
            title={blockedFromDeactivating ? S.whatsapp.cannotDeactivateDefault : undefined}
            onClick={() =>
              setActive({ variables: { id: account.id, isActive: !account.isActive } })
            }
          >
            {account.isActive ? S.whatsapp.deactivate : S.whatsapp.activate}
          </Button>
        </div>
      )}

      {blockedFromDeactivating && isAdmin && (
        <p className="text-xs text-text-muted">{S.whatsapp.cannotDeactivateDefault}</p>
      )}

      {pairing && <PairingPanel pairing={pairing} />}
      <MutationError error={unlinkState.error || defaultState.error || activeState.error} />

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={S.whatsapp.unlink}
        body={S.whatsapp.unlinkConfirm}
        confirmLabel={S.whatsapp.unlink}
        destructive
        onConfirm={async () => {
          await unlink({ variables: { accountId: account.accountId } });
          setConfirming(false);
        }}
      />

      <RenameDialog
        open={renaming}
        onOpenChange={setRenaming}
        account={account}
        onRenamed={onChanged}
      />
    </Card>
  );
}

/** The gateway's live half, shared by both card shapes so they cannot drift. */
function StatusFields({ status }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      <Field label={S.whatsapp.linkedNumber}>
        <span dir="ltr">{status.phoneE164 || S.app.none}</span>
      </Field>
      <Field label={S.whatsapp.pushName}>{status.pushName || S.app.none}</Field>
      <Field label={S.whatsapp.queueDepth}>{formatCount(status.queueDepth)}</Field>
      <Field label={S.whatsapp.sentLastHour}>
        {formatCount(status.sentLastHour)} / {formatCount(status.maxPerHour)}
      </Field>
      <Field label={S.whatsapp.lastError}>
        <span className="text-danger">{status.lastError || S.app.none}</span>
      </Field>
    </div>
  );
}

function RenameDialog({ open, onOpenChange, account, onRenamed }) {
  const [label, setLabel] = useState(account.label);
  const [rename, state] = useMutation(UPDATE_WHATSAPP_ACCOUNT, {
    onCompleted: () => {
      onRenamed();
      onOpenChange(false);
    },
  });
  useEffect(() => {
    if (open) setLabel(account.label);
  }, [open, account.label]);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={S.whatsapp.rename}
      submitLabel={S.app.save}
      busy={state.loading}
      canSubmit={Boolean(label.trim())}
      error={state.error}
      onSubmit={() => rename({ variables: { id: account.id, label: label.trim() } })}
    >
      <TextField
        label={S.whatsapp.accountLabel}
        hint={S.whatsapp.accountLabelHint}
        value={label}
        onChange={(event) => setLabel(event.target.value)}
      />
      {/* The id is shown and NOT editable: it keys the linked device and the
          routing key receipts arrive on, so there is no rename that is not an
          orphaning. Better to show it greyed than to omit it and leave people
          wondering where it went. */}
      <Field label={S.whatsapp.accountId}>
        <span dir="ltr" className="text-text-muted">
          {account.accountId}
        </span>
      </Field>
    </FormDialog>
  );
}

function AddAccountCard({ empty, onCreated }) {
  const [open, setOpen] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [label, setLabel] = useState('');
  const [create, state] = useMutation(CREATE_WHATSAPP_ACCOUNT, {
    onCompleted: () => {
      onCreated();
      setOpen(false);
      setAccountId('');
      setLabel('');
    },
  });

  return (
    <>
      <Card className="flex flex-wrap items-center gap-3">
        {empty && <p className="text-sm text-text-muted">{S.whatsapp.accountsEmpty}</p>}
        <Button variant="primary" onClick={() => setOpen(true)}>
          <Plus size={14} aria-hidden="true" />
          {S.whatsapp.addAccount}
        </Button>
      </Card>

      <FormDialog
        open={open}
        onOpenChange={setOpen}
        title={S.whatsapp.addAccount}
        hint={S.whatsapp.addAccountHint}
        submitLabel={S.app.save}
        busy={state.loading}
        // ONE required field. The id is the machine's job.
        canSubmit={Boolean(label.trim())}
        error={state.error}
        onSubmit={() =>
          create({
            variables: {
              label: label.trim(),
              // Empty means «derive it from the label», which is the path
              // essentially everybody takes.
              accountId: accountId.trim() || null,
            },
          })
        }
      >
        <TextField
          label={S.whatsapp.accountLabel}
          hint={S.whatsapp.accountLabelHint}
          value={label}
          onChange={(event) => setLabel(event.target.value)}
        />
        {/* THE ID IS DERIVED, AND THIS IS THE ESCAPE HATCH — closed, because
            the question it asks («what opaque prefixed identifier would you
            like?») is not one an administrator adding a phone number has an
            answer to. It exists for the one operator who does: somebody
            matching an id the gateway already holds. Same shape as the
            vocabulary creator, which derives a lookup's code and shows it
            rather than demanding it. */}
        <details className="text-xs">
          <summary className="cursor-pointer text-text-muted">
            {S.whatsapp.accountIdToggle}
          </summary>
          <div className="mt-2">
            <TextField
              label={S.whatsapp.accountId}
              hint={S.whatsapp.accountIdHint}
              value={accountId}
              dir="ltr"
              placeholder={S.whatsapp.accountIdPlaceholder}
              onChange={(event) => setAccountId(event.target.value)}
            />
          </div>
        </details>
      </FormDialog>
    </>
  );
}

function PairingPanel({ pairing }) {
  if (pairing.kind === 'PENDING') {
    return (
      <p className="flex items-center gap-2 text-sm text-text-muted">
        <Spinner /> {S.whatsapp.pairing}
      </p>
    );
  }
  if (pairing.kind === 'SUCCESS') {
    return <p className="text-sm text-success">{S.whatsapp.pairSuccess}</p>;
  }
  if (pairing.kind === 'TIMEOUT') {
    return <p className="text-sm text-warning">{S.whatsapp.pairTimeout}</p>;
  }
  if (pairing.kind === 'ERROR') {
    return <p className="text-sm text-danger">{pairing.message}</p>;
  }
  if (pairing.kind === 'PAIRING_CODE') {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-sm text-text-muted">{S.whatsapp.scanHint}</p>
        <p dir="ltr" className="text-xl font-bold tracking-widest">
          {pairing.code}
        </p>
      </div>
    );
  }
  // QR. Drawn as a scannable code since 2026-09-06; it used to be the payload
  // text and an instruction to paste it elsewhere (ui.md).
  //
  // THE TEXT SURVIVES, behind a disclosure. It is what a support call falls
  // back to when the screen will not scan — a glare, a scaled-down window, a
  // camera that cannot focus that close — and it costs nothing to keep. Closed
  // by default, because offering both as equals would suggest the operator has
  // a decision to make here.
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-text-muted">{S.whatsapp.scanHint}</p>
      <QrCode value={pairing.code} label={S.whatsapp.qrLabel} />
      <CountDown seconds={pairing.expiresInSeconds} />
      <details className="text-xs">
        <summary className="cursor-pointer text-text-muted">
          {S.whatsapp.qrPayloadToggle}
        </summary>
        <code
          dir="ltr"
          className="mt-2 block max-h-32 overflow-auto break-all rounded border
                     border-border bg-bg p-3 text-xs"
        >
          {pairing.code}
        </code>
      </details>
    </div>
  );
}

/** The countdown that answers «چرا هیچ اتفاقی نمی‌افتد». */
function CountDown({ seconds }) {
  const [left, setLeft] = useState(seconds ?? 0);
  useEffect(() => {
    setLeft(seconds ?? 0);
    const timer = setInterval(() => setLeft((v) => (v > 0 ? v - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, [seconds]);
  return <p className="text-xs text-text-muted tabular-nums">{formatCount(left)}s</p>;
}
